import { useMemo, useState, useEffect } from 'react'

import { type IFileViewerProps } from './FileViewer'
import { type IFileData } from '../File/File'

interface UseFileViewerStateOptions<FileType = IFileData> {
  selectedFileProp: IFileViewerProps<FileType>['selectedFile']
  onSelectedFileChangeProp: IFileViewerProps<FileType>['onSelectedFileChange']
  files: FileType[]
  defaultFirstFile: boolean
  initialSelectedFile: IFileViewerProps<FileType>['selectedFile']
  isFileSelected: IFileViewerProps<FileType>['isFileSelected']
}

export const useFileViewerState = <FileType = IFileData>({
  selectedFileProp,
  onSelectedFileChangeProp,
  files,
  defaultFirstFile,
  initialSelectedFile = null,
  isFileSelected,
}: UseFileViewerStateOptions<FileType>) => {
  const isControlled = useMemo(() => selectedFileProp !== undefined, [])

  const [uncontrolledSelectedFile, setUncontrolledSelectedFile] =
    useState<IFileViewerProps<FileType>['selectedFile']>(initialSelectedFile)

  const resolvedSelectedFile = useMemo(() => {
    if (!uncontrolledSelectedFile) return uncontrolledSelectedFile

    return (
      files?.find((file) => isFileSelected(file, uncontrolledSelectedFile)) ??
      uncontrolledSelectedFile
    )
  }, [files, uncontrolledSelectedFile, isFileSelected])

  const selectedFile = isControlled ? selectedFileProp : resolvedSelectedFile

  const onSelectedFileChangeFn: IFileViewerProps<FileType>['onSelectedFileChange'] =
    isControlled
      ? onSelectedFileChangeProp
      : (file) => {
          onSelectedFileChangeProp?.(file)
          setUncontrolledSelectedFile(file)
        }

  /**
   * If [defaultFile=true] and not file is selected,
   * select the first file in the list
   */
  useEffect(() => {
    if (!files?.length || uncontrolledSelectedFile || !defaultFirstFile) return
    onSelectedFileChangeFn(files[0])
  }, [files, uncontrolledSelectedFile, defaultFirstFile])

  /**
   * If the selected file is not in the list of files,
   * set the selected file to null
   */
  useEffect(() => {
    if (isControlled || !uncontrolledSelectedFile) return

    if (files.find((file) => isFileSelected(file, uncontrolledSelectedFile)))
      return

    setUncontrolledSelectedFile(null)
  }, [files, isControlled])

  return {
    selectedFile,
    onSelectedFileChange: onSelectedFileChangeFn,
  }
}
